import { createFile } from 'mp4box';

import type { MP4ArrayBuffer, MP4Info } from 'mp4box';

/**
 * Tier 1 — container metadata via mp4box.
 *
 * Parses the `moov` atom to read video dimensions, duration, and audio-track
 * presence WITHOUT decoding the media, so it works for every codec (H.264,
 * HEVC, ProRes, …). Only a small slice of the file is read (the head, and the
 * tail if the moov is stored there — common for QuickTime/iPhone `.mov`).
 */

export interface ContainerMetadata {
  width: number;
  height: number;
  durationSec: number;
  hasAudioTrack: boolean;
}

/** Bytes read per probe. moov is almost always smaller than this. */
const PROBE_BYTES = 16 * 1024 * 1024;

/**
 * Pure mapping from mp4box info → our metadata shape (unit-testable).
 *
 * `trackHandlers` are the tracks' `hdlr` handler types. Audio presence also
 * counts any `soun` track, because mp4box only lists tracks whose sample entry
 * it recognizes in `audioTracks` — QuickTime PCM (`lpcm`, typical of NLE and
 * camera exports) comes back as a `metadata` track and would read as silent.
 */
export function evaluateContainer(
  info: MP4Info,
  trackHandlers: string[] = [],
): ContainerMetadata {
  const video = info.videoTracks[0];
  const width = video?.video?.width ?? video?.track_width ?? 0;
  const height = video?.video?.height ?? video?.track_height ?? 0;
  const durationSec = info.timescale ? info.duration / info.timescale : 0;
  return {
    width,
    height,
    durationSec,
    hasAudioTrack:
      info.audioTracks.length > 0 || trackHandlers.includes('soun'),
  };
}

async function appendRange(
  file: File,
  mp4: ReturnType<typeof createFile>,
  start: number,
  end: number,
): Promise<void> {
  const buf = await file.slice(start, end).arrayBuffer();
  const chunk: MP4ArrayBuffer = Object.assign(buf, { fileStart: start });
  mp4.appendBuffer(chunk);
}

/**
 * Read container metadata by parsing the moov atom. Returns null if the moov
 * can't be parsed within the probed ranges — the caller fail-closes on null.
 */
export async function readContainerMetadata(
  file: File,
): Promise<ContainerMetadata | null> {
  return new Promise(resolve => {
    const mp4 = createFile();
    let settled = false;
    const settle = (value: ContainerMetadata | null): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    mp4.onReady = info =>
      settle(
        evaluateContainer(
          info,
          mp4.moov?.traks.map(trak => trak.mdia.hdlr.handler),
        ),
      );
    mp4.onError = () => settle(null);

    void (async () => {
      try {
        // Probe the head (faststart files carry the moov near the front).
        await appendRange(file, mp4, 0, Math.min(PROBE_BYTES, file.size));
        if (settled) return;

        // Otherwise probe the tail (QuickTime/iPhone .mov store moov at the end).
        if (file.size > PROBE_BYTES) {
          const tailStart = Math.max(PROBE_BYTES, file.size - PROBE_BYTES);
          await appendRange(file, mp4, tailStart, file.size);
        }
        mp4.flush();

        // Neither probe yielded a moov → fail closed.
        settle(null);
      } catch {
        settle(null);
      }
    })();
  });
}

const WEBM_METADATA_TIMEOUT_MS = 10_000;

/** `audioTracks` / `mozHasAudio` are non-standard; absent ⇒ assume audio. */
function webmHasAudio(video: HTMLVideoElement): boolean {
  if ('audioTracks' in video && video.audioTracks) {
    const { length } = video.audioTracks as { length?: number };
    if (typeof length === 'number') return length > 0;
  }
  if ('mozHasAudio' in video && typeof video.mozHasAudio === 'boolean') {
    return video.mozHasAudio;
  }
  return true;
}

/**
 * WebM has no `moov`, so mp4box can't read it — ask the browser's demuxer for
 * dimensions and duration instead. Fail-closed like the MP4 path: a file the
 * browser can't open is rejected. Audio presence comes from `audioTracks` /
 * `mozHasAudio` where the browser exposes them; otherwise it is assumed present
 * and the (fail-open) silence check is the remaining guard.
 */
export async function readWebmMetadata(
  file: File,
): Promise<ContainerMetadata | null> {
  if (typeof document === 'undefined') return null;

  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;

  try {
    return await new Promise<ContainerMetadata | null>(resolve => {
      const timer = setTimeout(() => resolve(null), WEBM_METADATA_TIMEOUT_MS);
      const done = (value: ContainerMetadata | null): void => {
        clearTimeout(timer);
        resolve(value);
      };
      video.onerror = () => done(null);
      video.onloadedmetadata = () => {
        const hasAudioTrack = webmHasAudio(video);
        done({
          width: video.videoWidth,
          height: video.videoHeight,
          // MediaRecorder WebM often reports Infinity until fully scanned.
          durationSec: Number.isFinite(video.duration) ? video.duration : 0,
          hasAudioTrack,
        });
      };
      video.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
    video.removeAttribute('src');
    video.load();
  }
}
